// components/shared/GDImageViewer.js
//
// Google-Drive powered image grid + light-box.
//
//  ▸ Lists all images in a Drive directory (no pagination).
//  ▸ Renders a responsive thumbnail grid.
//  ▸ Click → full-screen modal (Esc / click-outside to close).
//  ▸ Optional prev / next arrows + ← / → keys.
//  ▸ Background scroll is frozen while the modal is open.
//  ▸ Fully styleable through `classes` + `styles` props.
// -------------------------------------------------------------------

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import Loading from "./Loading";
import "../../assets/styles/GDImageViewer.css";

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

export default function GDImageViewer({
  /* == props ======================================================= */
  data,                         // ► mandatory Google-Drive config
  className = "",               // ► extra class(es) for root
  classes: variantClasses = {}, // ► slot class overrides
  styles: slotStyles = {},    // ► slot style overrides
  ImageComponent = null,        // ► custom per-image renderer
}) {
  /* == pull all options up-front =================================== */
  const {
    dirId,
    gkey,
    header,
    options: {
      imgStyle = {},
      hover = false,
      onClick = {},
      switchArrows = false,
      attachClass = {},
      attachId = {},
      exclude = {},
      thumbSize = 400,
      gridOriginal = false,
      showDate = false,
      loadingText = "",
    },
  } = data;

  const useModal = Boolean(onClick.modal);
  const clickable = !useModal && Boolean(onClick.newWindow || onClick);
  const openInNewTab = Boolean(onClick.newWindow);

  /* == slot class / style maps  ==================================== */
  const defaultClasses = {
    root: "gd-image-viewer-root",
    gallery: "galleryImgContainers",
    thumbWrapper: "gd-thumb-wrapper",
    image: "gd-img",
    dateBadge: "gd-date-badge",
    modal: "gdv-modal",
    modalContent: "gdv-modal-content",
    closeButton: "gdv-close",
    arrow: "gdv-arrow",
    prev: "gdv-prev",
    next: "gdv-next",
  };
  const defaultStyles = {
    root: {},
    gallery: {},
    thumbWrapper: {},
    image: imgStyle,
    dateBadge: {},
    modal: {},
    modalContent: {},
    closeButton: {},
  };
  const cls = { ...defaultClasses, ...variantClasses };
  const sty = { ...defaultStyles, ...slotStyles };

  /* == reactive state ============================================== */
  const [items, setItems] = useState([]);    // ↳ Drive file list
  const [loading, setLoading] = useState(true);  // ↳ list still loading?
  const [activeSrc, setActiveSrc] = useState(null);  // ↳ modal image URL
  const [activeIdx, setActiveIdx] = useState(-1);    // ↳ index in `items`
  const closeBtnRef = useRef(null);                   // ↳ focus trap later?

  /* ---------------------------------------------------------------- *
   *  1. Fetch file list (once dirId / gkey change)                   *
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({
          q: `'${dirId}' in parents and trashed=false`,
          fields: "files(id,name,mimeType,modifiedTime)",
          key: gkey,
          pageSize: 1000,
        });
        const res = await fetch(
          `https://www.googleapis.com/drive/v3/files?${params}`,
          { signal: ctrl.signal }
        );
        const { files = [] } = await res.json();
        setItems(files.filter(f => f.mimeType.startsWith("image/")));
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [dirId, gkey]);

  /* ---------------------------------------------------------------- *
   *  2. Build thumbnail / full URLs                                  *
   *     (2048 px from Drive’s CDN avoids CAPTCHA & is fast)          *
   * ---------------------------------------------------------------- */
  const DRIVE_THUMB = "https://drive.google.com/thumbnail?id=";
  const FULL_SZ_PX = 2048;
  const openSrc = useCallback(
    (id, large = false) => {
      const sz = large || gridOriginal ? FULL_SZ_PX : thumbSize;
      return `${DRIVE_THUMB}${id}&sz=w${sz}`;
    },
    [gridOriginal, thumbSize]
  );

  /* ---------------------------------------------------------------- *
   *  3. Helpers                                                      *
   * ---------------------------------------------------------------- */
  // simple debounce used for hover-preview (non-modal mode)
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  // small preview if gallery isn’t modal
  const debouncedPreview = useMemo(
    () =>
      debounce(id => {
        setActiveSrc(openSrc(id, true));
        setActiveIdx(-1);                 // disable arrows
      }, 300),
    [openSrc]
  );

  // close the modal (used by ×, overlay click, Esc key)
  const closeModal = useCallback(() => {
    setActiveSrc(null);
    setActiveIdx(-1);
  }, []);

  // generic click handler (grid item)
  const handleClick = useCallback(
    id => {
      const idx = items.findIndex(it => it.id === id);

      /* ⇢ 1) modal light-box -------------------------------------- */
      if (useModal) {
        setActiveSrc(openSrc(id, true));
        setActiveIdx(idx);
        return;
      }

      /* ⇢ 2) open in new tab / same tab --------------------------- */
      if (clickable) {
        const href = openSrc(id);
        openInNewTab ? window.open(href, "_blank")
          : (window.location.href = href);
        return;
      }

      /* ⇢ 3) plain hover preview ---------------------------------- */
      debouncedPreview(id);
    },
    [useModal, clickable, openInNewTab, openSrc, debouncedPreview, items]
  );

  /* == arrow navigation =========================================== */
  const prevImage = useCallback(() => {
    setActiveIdx(i => {
      const newIdx = (i - 1 + items.length) % items.length;
      setActiveSrc(openSrc(items[newIdx].id, true));
      return newIdx;
    });
  }, [items, openSrc]);

  const nextImage = useCallback(() => {
    setActiveIdx(i => {
      const newIdx = (i + 1) % items.length;
      setActiveSrc(openSrc(items[newIdx].id, true));
      return newIdx;
    });
  }, [items, openSrc]);

  /* == pre-load neighbour images for snappy nav =================== */
  useEffect(() => {
    if (!switchArrows || activeIdx === -1) return;
    [(activeIdx + 1) % items.length,
    (activeIdx - 1 + items.length) % items.length].forEach(i => {
      const img = new Image();
      img.src = openSrc(items[i].id, true);
    });
  }, [activeIdx, switchArrows, items, openSrc]);

  /* == global key-handler (Esc + arrows) ========================== */
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") return closeModal();
      if (activeIdx === -1) return;        // nothing open
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keyup", onKey);
    return () => window.removeEventListener("keyup", onKey);
  }, [activeIdx, prevImage, nextImage, closeModal]);

  /* == lock page scroll while modal is open ======================= */
  useEffect(() => {
    const styleTagId = "gd-image-viewer-scroll-lock";

    const preventScroll = (e) => e.preventDefault();

    if (activeSrc) {
      // Inject <style> to disable scroll
      const style = document.createElement("style");
      style.id = styleTagId;
      style.textContent = `body { overflow: hidden !important; }`;
      document.head.appendChild(style);

      // Add listeners to block all scroll
      document.addEventListener("wheel", preventScroll, { passive: false });
      document.addEventListener("touchmove", preventScroll, { passive: false });

      return () => {
        // Clean up on modal close
        document.removeEventListener("wheel", preventScroll, { passive: false });
        document.removeEventListener("touchmove", preventScroll, { passive: false });
        const styleTag = document.getElementById(styleTagId);
        if (styleTag) styleTag.remove();
      };
    }
  }, [activeSrc]);


  /* ---------------------------------------------------------------- *
   *  4. Modal portal                                                 *
   * ---------------------------------------------------------------- */
  const modal = activeSrc && createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className={cls.modal}
      style={sty.modal}
      onClick={closeModal /* click-outside closes */}
    >
      {/* close × --------------------------------------------------- */}
      <button
        ref={closeBtnRef}
        aria-label="Close"
        className={cls.closeButton}
        style={sty.closeButton}
        onClick={closeModal}
      >
        &times;
      </button>

      {/* prev arrow ------------------------------------------------ */}
      {switchArrows && (
        <button
          className={`${cls.arrow} ${cls.prev}`}
          aria-label="Previous"
          onClick={e => { e.stopPropagation(); prevImage(); }}
        >‹</button>
      )}

      {/* full-size image ------------------------------------------ */}
      <img
        src={activeSrc}
        alt="Full size"
        className={cls.modalContent}
        style={sty.modalContent}
        onClick={e => e.stopPropagation() /* don’t close on img */}
      />

      {/* next arrow ------------------------------------------------ */}
      {switchArrows && (
        <button
          className={`${cls.arrow} ${cls.next}`}
          aria-label="Next"
          onClick={e => { e.stopPropagation(); nextImage(); }}
        >›</button>
      )}
    </div>,
    document.body
  );

  /* ---------------------------------------------------------------- *
   *  5. Render grid (or loader)                                      *
   * ---------------------------------------------------------------- */
  if (loading) return <Loading text={loadingText} />;

  return (
    <div className={`${cls.root} ${className}`.trim()} style={sty.root}>
      {header && <h2>{header}</h2>}
      {modal /* ← portal renders at body root, but we keep ref here */}
      <div className={cls.gallery} style={sty.gallery}>
        {items.map(item => {
          if (exclude[item.name]) return null;

          const props = {
            item,
            openSrc,
            onClick: () => handleClick(item.id),
            classes: cls,
            styles: sty,
            hover,
            attachClass,
            attachId,
            showDate,
          };

          const Component = ImageComponent || DefaultImageRenderer;

          return <Component key={item.id} {...props} />;   // ✅ key is safe here
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Default thumbnail renderer (fallback)                             *
 * ------------------------------------------------------------------ */
function DefaultImageRenderer({
  item: { id, name, modifiedTime },
  openSrc,
  onClick,
  classes,
  styles,
  hover,
  attachClass,
  attachId,
  showDate,
}) {
  const formattedDate =
    showDate && modifiedTime
      ? new Date(modifiedTime).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
      : "";

  return (
    <div className={classes.thumbWrapper} style={styles.thumbWrapper}>
      {formattedDate && (
        <span className={classes.dateBadge} style={styles.dateBadge}>
          {formattedDate}
        </span>
      )}
      <img
        src={openSrc(id)}
        alt={name}
        className={[
          classes.image,
          hover && "gd-hover",
          attachClass[name],
        ].filter(Boolean).join(" ")}
        style={styles.image}
        id={attachId[name]}
        onClick={onClick}
      />
    </div>
  );
}
